import React, { useState, useEffect, useId, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import useListboxNav from '../hooks/useListboxNav';

// frontend/CLAUDE.md:18-22 — "URL Rules (CRITICAL)": API calls go through
// API_URL from AuthContext, which builds it at RUNTIME from window.location
// (AuthContext.js:13-24). Never process.env.REACT_APP_API_URL — it is baked
// at build time.
const MIN_QUERY = 2;
const DEBOUNCE_MS = 250;

// Response contract (verified against admin_search.py + the router's dict
// assembly — see task-3-report.md "Contract notes for Task 4"):
//   { query, groups: [{ key, label, results, has_more }] }
// groups is always the fixed 3-element [users, schools, camps], always
// present even when empty. Result shape differs by `type`:
//   user / pending_guardian -> id, name, email, role, detail, url
//   high_school / college / camp -> id, name, detail, url (no email/role)
// This component only ever reads name, detail, url, and type+id (for the
// React key) — every key it touches is in that table.

export default function AdminSearchBar() {
  const { API_URL } = useAuth();
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState(null);
  // Distinct from "groups came back empty" — a failed request must never
  // render as "No matches", because an admin reading that concludes the
  // record does not exist and closes the ticket. See fix-report: this phase
  // exists partly so an admin can confirm a pending guardian invite is real;
  // a silent-failure "No matches" is wrong in the most consequential
  // direction available.
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    // Below the minimum we do not call the API at all — a one-character
    // query matches most of the database and is never what was meant.
    if (query.trim().length < MIN_QUERY) {
      setGroups(null);
      setError(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      axios
        .get(`${API_URL}/admin/search?q=${encodeURIComponent(query.trim())}`)
        .then(({ data }) => {
          if (!cancelled) {
            setGroups(data.groups);
            setError(false);
            setOpen(true);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setGroups(null);
            setError(true);
            setOpen(true);
          }
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, API_URL]);

  useEffect(() => {
    const onClickAway = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const go = (url) => {
    setOpen(false);
    setGroups(null);
    setError(false);
    setQuery('');
    navigate(url);
  };

  const hasAny = !!groups && groups.some((g) => g.results.length > 0);
  const nonEmptyGroups = groups ? groups.filter((g) => g.results.length > 0) : [];

  // Every result in the order it is drawn. Keyboard navigation walks this list;
  // a result with no url (schools/colleges) is a row but cannot be opened, so the
  // arrows skip it.
  const rows = nonEmptyGroups.flatMap((g) => g.results);
  const listOpen = open && !error && hasAny;
  const baseId = useId();
  const listId = `${baseId}-list`;
  const rowId = (i) => `${baseId}-row-${i}`;

  const nav = useListboxNav({
    count: rows.length,
    open: listOpen,
    setOpen,
    onPick: (i) => go(rows[i].url),
    isDisabled: (i) => !rows[i].url,
  });

  let rowIndex = -1;   // running index across groups, matching `rows`

  return (
    <div className="adm-search" ref={boxRef}>
      <input
        type="search"
        role="combobox"
        className="adm-search__input"
        placeholder="Search athletes, coaches, schools, camps…"
        aria-label="Search"
        aria-expanded={listOpen}
        aria-controls={listOpen ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={listOpen && nav.highlight >= 0 ? rowId(nav.highlight) : undefined}
        value={query}
        onChange={(e) => { nav.reset(); setQuery(e.target.value); }}
        onFocus={() => (groups || error) && setOpen(true)}
        onKeyDown={nav.onKeyDown}
      />

      {open && (error || groups) && (
        <div className="adm-search__results">
          {error && (
            <div className="adm-search__error" role="status">
              Search is unavailable right now — try again.
            </div>
          )}
          {!error && !hasAny && (
            <div className="adm-search__empty" role="status">No matches for "{query}".</div>
          )}
          {!error && hasAny && (
            <div id={listId} role="listbox" aria-label="Search results">
              {nonEmptyGroups.map((group) => (
                <div className="adm-search__group" key={group.key}>
                  <div className="adm-search__grouplabel" role="presentation">{group.label}</div>
                  {group.results.map((r) => {
                    rowIndex += 1;
                    const i = rowIndex;
                    return r.url ? (
                      <button
                        type="button"
                        role="option"
                        id={rowId(i)}
                        aria-selected={i === nav.highlight}
                        className={`adm-search__result${i === nav.highlight ? ' adm-search__result--active' : ''}`}
                        key={`${r.type}-${r.id}`}
                        onClick={() => go(r.url)}
                        onMouseEnter={() => nav.setHighlight(i)}
                      >
                        <span className="adm-search__name">{r.name}</span>
                        <span className="adm-search__detail">{r.detail}</span>
                      </button>
                    ) : (
                      // F2: schools and colleges have no reliable admin
                      // destination (see admin_search.py) — url is None, and
                      // this must render as inert: not a <button>, not
                      // clickable, not an option, and skipped by the arrow keys,
                      // rather than promising a navigation that fails.
                      <div
                        className="adm-search__result adm-search__result--static"
                        key={`${r.type}-${r.id}`}
                      >
                        <span className="adm-search__name">{r.name}</span>
                        <span className="adm-search__detail">{r.detail}</span>
                      </div>
                    );
                  })}
                  {group.has_more && (
                    <div className="adm-search__more">
                      More matches — narrow the search to see them.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
